import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

// The name shown before anyone has signed in. One login page serves every
// company, so it cannot name one of them - and it must not name the company
// that BUILT it either: a white-label product sold on should not put the
// vendor in front of the customer's own staff every morning.
// Change it here; nothing else reads it.
export const PRODUCT_NAME = 'Shopfloor';

const BrandingContext = createContext(null);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  // Fetched after sign-in rather than read from the user saved at login. A
  // session lasts 30 days, and a company that changes its name or logo should
  // not have to wait that long to be shown correctly.
  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated) {
      setCompany(null);
      return undefined;
    }

    api.get('/branding')
      .then((res) => {
        if (!cancelled) setCompany(res.data.company || null);
      })
      .catch(() => {
        // Branding is decoration. If it cannot be loaded the dashboard still
        // works, under the product name.
        if (!cancelled) setCompany(null);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // The logo route requires the session, and a plain <img src> would not send
  // it, so the bytes are fetched like any other call and handed to the browser
  // as an object URL. Revoked on change so the memory is not held.
  useEffect(() => {
    let cancelled = false;
    let created = null;

    if (!company?.has_logo) {
      setLogoUrl(null);
      return undefined;
    }

    api.get('/branding/logo', { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        created = URL.createObjectURL(res.data);
        setLogoUrl(created);
      })
      .catch(() => {
        if (!cancelled) setLogoUrl(null);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [company]);

  // What the window is called. Before sign-in, and for a user with no company,
  // this is the product name.
  const displayName = company?.name || PRODUCT_NAME;

  useEffect(() => {
    document.title = displayName;
  }, [displayName]);

  // The accent, when a company has chosen one, is applied as a CSS variable so
  // stylesheets can pick it up without any component knowing about it.
  useEffect(() => {
    const root = document.documentElement;
    if (company?.accent_color) {
      root.style.setProperty('--company-accent', company.accent_color);
    } else {
      root.style.removeProperty('--company-accent');
    }
  }, [company]);

  // The tab icon follows the logo when there is one.
  useEffect(() => {
    if (!logoUrl) return undefined;

    const link = document.querySelector("link[rel~='icon']")
      || Object.assign(document.createElement('link'), { rel: 'icon' });
    const previous = link.getAttribute('href');
    link.href = logoUrl;
    if (!link.parentNode) document.head.appendChild(link);

    return () => {
      if (previous) link.href = previous;
    };
  }, [logoUrl]);

  const value = {
    company,
    displayName,
    logoUrl,
    productName: PRODUCT_NAME
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};
